// #region Imports
import { MdSupervisorAccount, MdOutlineSupportAgent } from "react-icons/md";
import { FaUserTie, FaTruck, FaUserCog, FaUser } from "react-icons/fa";
import { FaPersonRunning } from "react-icons/fa6";

import { PiUsersThreeBold } from "react-icons/pi";
// #endregion


const BuggyDriverIconComponent = ({ size = "1.5em", color = "currentColor", ...props }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
        width={size}
        height={size}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        {/* Flat canopy / roof */}
        <line x1="10" y1="18" x2="46" y2="18" />

        {/* Rear roof support post */}
        <line x1="12" y1="18" x2="12" y2="30" />

        {/* Middle support post */}
        <line x1="29" y1="18" x2="29" y2="30" />

        {/* Front roof support post */}
        <line x1="46" y1="18" x2="46" y2="32" />

        {/* Seat back */}
        <path d="M14 30 L14 26 L26 26 L26 30" />

        {/* Body / chassis */}
        <path d="M8 38 L8 34 L46 34 L52 38 L52 42 L8 42 Z" />

        {/* Front wheel */}
        <circle cx="44" cy="46" r="6" />
        <circle cx="44" cy="46" r="1.5" />

        {/* Rear wheel */}
        <circle cx="16" cy="46" r="6" />
        <circle cx="16" cy="46" r="1.5" />
    </svg>
);

export default BuggyDriverIconComponent;
// #region Timeline Column Dimensions (source of truth)
// Adjust these two base values to resize the entire timeline proportionally
export const TimelineColumnWidthRaw = 215;
export const TimelineColumnHeightRaw = 85;
export const TimelineColumnWidth = `${TimelineColumnWidthRaw}px`;
export const TimelineColumnHeight = `${TimelineColumnHeightRaw}px`;
// #endregion

// #region Derived: 30-Minute Layout
// Each column spans 30 minutes — all widths scale from column width
export const minuteWidth = TimelineColumnWidthRaw / 30;
export const minute30MinLayoutWidth = minuteWidth;

export const Timeline30MinLayoutColumnWidth = TimelineColumnWidth;
export const Timeline30MinLayoutColumnWidthRaw = TimelineColumnWidthRaw;

export const JobCard30MinLayoutWidth = TimelineColumnWidthRaw;
export const JobCard30MinLayoutHeight = Math.round(TimelineColumnHeightRaw * 0.59);

export const Job30MinLayoutCardWidth = TimelineColumnWidthRaw;
export const Job30MinLayoutCardHeight = Math.round(TimelineColumnHeightRaw * 0.59);
// #endregion

// #region Derived: Standard Layout
export const JobCardWidth = TimelineColumnWidthRaw / 2;
export const JobCardHeight = Math.round(TimelineColumnHeightRaw * 1.76);
export const EmployeeJobCardWidth = Math.round(TimelineColumnWidthRaw * 0.698);
// #endregion

// #region Phone layout
// Read once when the timeline loads. Crossing the breakpoint (e.g. rotating a tablet)
// takes effect on the next page load, which keeps every column width below static.
export const IS_PHONE_TIMELINE =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 767.98px)').matches;
// #endregion

// #region Derived: Employee Info Column
// The staff name column. NOT the width of a time column — use TimelineColumnWidth(Raw) for that.
// Narrower on phones so more of the timeline itself is visible.
export const EmployeeInformationColumnWidthRaw = IS_PHONE_TIMELINE ? 120 : TimelineColumnWidthRaw;
export const EmployeeInformationColumnWidth = `${EmployeeInformationColumnWidthRaw}px`;
// #endregion

// #region Time Arrays
// 30-min interval slots across 48 hours (96 entries) to support past-midnight schedules
export const Timeline30MinLayoutHours = Array.from({ length: 96 }, (_, index) => {
    const hour = Math.floor(index / 2);
    const minutes = index % 2 === 0 ? "00" : "30";
    return `${hour}:${minutes}`;
});

export const TimelineHours = [
    "0:00", "1:00", "2:00", "3:00", "4:00", "5:00",
    "6:00", "7:00", "8:00", "9:00", "10:00", "11:00",
    "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
    "18:00", "19:00", "20:00", "21:00", "22:00", "23:00",
];

export const EmployeeScannerHours = [
    "00:00", "01:00", "02:00", "03:00", "04:00", "05:00",
    "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
    "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
    "18:00", "19:00", "20:00", "21:00", "22:00", "23:00",
];
// #endregion

// #region Airport Codes
export const SaudiDomesticAirports = [
    "JED", "RUH", "DMM", "MED", "AHB", "AQI", "ABT",
    "EJH", "SHW", "BHH", "WAE", "DWD", "EAM", "GIZ",
    "HAS", "AJF", "TIF", "ULH", "TUI", "RAE", "RAH",
    "URY", "TUU", "RSI", "NUM", "HOF", "ELQ", "YNB",
    "QJB",
];
// #endregion

// #region Booking Type Filters
export const BookingTypesFilters = [
    { label: "All", value: "all" },
    { label: "Departures", value: "departures" },
    { label: "Arrivals", value: "arrivals" },
    { label: "ALT Pick Up", value: "ALTPickUp" },
    { label: "Gate Relocation", value: "gateRelocation" },
    // { label: "Checked In", value: "checkedIn" },
    { label: "Boarding", value: "boarding" },
    { label: "Ramp", value: "ramp" },
];
// #endregion

// #region Timeline Roles
// TODO: clarify what role maps to "staff", or deprecate
export const timelineRoles = [
    { label: "Supervisor", value: "manager", icon: <MdSupervisorAccount /> },
    { label: "Allocator", value: "allocator", icon: <FaUserCog /> },
    { label: "Driver", value: "driver", icon: <FaTruck /> },
    { label: "CCA", value: "cca", icon: <FaUserTie /> },
    { label: "Boarding Agent", value: "boarding", icon: <PiUsersThreeBold /> },
    { label: "Marshaller", value: "marshaller", icon: <FaUser /> },
    { label: "Desk Host", value: "desk", icon: <MdOutlineSupportAgent /> },
    { label: "Buggy Driver", value: "buggyDriver", icon: <BuggyDriverIconComponent /> },
    { label: "Runner", value: "runner", icon: <FaPersonRunning /> },

    // { label: "Baggage Handler", value: "baggage-handler", icon: <LuBaggageClaim /> },
];
// #endregion
